import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { DevistoLogo } from "@/components/DevistoLogo";
import { apiError, inputCls, btnPrimary } from "@/lib/studioApi";
import { PageSkeleton } from "@/components/studio/StudioUI";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StudioInvitation() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/studio/invitation/${token}`)
      .then(({ data }) => {
        setInvitation(data);
        setForm((f) => ({ ...f, name: data.name || "" }));
      })
      .catch((e) => setError(apiError(e, "Invitation introuvable ou déjà utilisée.")));
  }, [token]);

  const accept = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/studio/invitation/${token}`, form);
      localStorage.setItem("devisto_token", data.token);
      localStorage.setItem("devisto_token_at", String(Date.now()));
      localStorage.setItem("devisto_role", data.role);
      localStorage.setItem("devisto_name", data.name || "");
      localStorage.setItem("devisto_member_role", data.member_role || "");
      toast.success(data.member_role === "comptable" ? "Bienvenue ! Voici l'espace comptable." : "Bienvenue dans le Studio !");
      navigate(data.member_role === "comptable" ? "/studio/comptable" : "/studio");
    } catch (err) {
      toast.error(apiError(err, "Activation impossible."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="invitation-page" className="font-body min-h-screen bg-[#F1F5F9] flex flex-col items-center justify-center px-5 py-16">
      <DevistoLogo className="h-8 w-auto" />
      <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#3A5CC8]">Studio</p>

      {error ? (
        <div className="mt-10 max-w-md text-center">
          <p data-testid="invitation-error" className="text-sm text-red-600">{error}</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link to="/connexion" data-testid="invitation-login-link" className={`${btnPrimary}`}>Se connecter</Link>
            <Link to="/" data-testid="invitation-home-link" className="text-sm font-semibold text-[#3A5CC8] hover:underline">
              Retour à l'accueil
            </Link>
          </div>
        </div>
      ) : !invitation ? (
        <PageSkeleton />
      ) : (
        <div className="mt-10 w-full max-w-md rounded-2xl border border-[#E2E7F7] bg-white p-7 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-[#0F2645]">
            {invitation.company || "Votre société"} vous invite
          </h1>
          <p data-testid="invitation-intro" className="mt-3 text-sm text-[#5C6A87] leading-relaxed">
            {invitation.member_role === "comptable"
              ? "Choisissez votre mot de passe pour consulter les factures de vente (PDF + UBL), les achats et l'export comptable de la société."
              : "Choisissez votre mot de passe pour accéder aux dossiers de chantier de la société."}
          </p>
          <form onSubmit={accept} className="mt-6 space-y-4">
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-[#5C6A87] mb-1">Email</label>
              <input data-testid="invitation-email" className={`${inputCls} bg-[#F6F8FD]`} value={invitation.email} disabled />
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-[#5C6A87] mb-1">Votre nom</label>
              <input
                data-testid="invitation-name"
                className={inputCls}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Prénom et nom"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-[#5C6A87] mb-1">Mot de passe</label>
              <input
                data-testid="invitation-password"
                type="password"
                className={inputCls}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="6 caractères minimum"
                required
              />
            </div>
            <button type="submit" data-testid="invitation-submit" disabled={busy} className={`${btnPrimary} w-full justify-center`}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Activer mon accès
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
